import { ChevronRight } from "lucide-react";
import { Textarea } from "@ncoder/ui";
import type { ClarifyAnswer, ClarifyCandidate, ClarifyQuestion, Option } from "@/services/multi-agent/protocol";
import { FREE_TEXT_MAX } from "@/services/multi-agent/interactions";
import { OptionCards } from "./OptionCards";

/**
 * A batch clarify's sub-questions (contracts 0.2.9, `Interaction.questions`) — 2 to 5 related
 * questions asked in ONE interaction, answered together as `InteractionRespondBody.answers`.
 *
 * ONE FORM, NOT FIVE CARDS. The batch exists so the run asks everything it needs in one pause
 * rather than five round trips, and the reader should see it as one decision with parts. Each
 * sub-question gets a numbered heading, its own options / candidates / free text, and its own
 * "if you skip" line; the card above owns the single submit.
 *
 * PURE PROPS. The answers live with the card, which submits them — same reason `InteractionBody`
 * lifts `provide_input`'s values. This renders and reports; it never posts.
 *
 * ONE ANSWER PER QUESTION. An option pick, a candidate pick and free text are three ways to say one
 * thing, so choosing one clears the others — a body carrying both `option_id` and `ref_id` for the
 * same question is legal on the wire but says two things at once, and the server would have to
 * guess which the reader meant.
 */
export interface ClarifyQuestionsProps {
  questions: readonly ClarifyQuestion[];
  answers: Record<string, ClarifyAnswer>;
  onChange: (questionId: string, answer: ClarifyAnswer | null) => void;
  disabled?: boolean;
  idPrefix: string;
}

/** Candidates as options, so the radio group's keyboard and a11y contract is reused verbatim. */
export function candidatesAsOptions(candidates: readonly ClarifyCandidate[]): Option[] {
  return candidates.map((c) => ({
    option_id: c.ref_id,
    label: c.label,
    // The kind is the sub-copy: "Opportunity_Line__c — field" tells the reader what they are picking.
    description: c.ref_kind,
  }));
}

export function ClarifyQuestions({ questions, answers, onChange, disabled, idPrefix }: ClarifyQuestionsProps) {
  return (
    <ol className="mt-3 flex flex-col gap-3.5">
      {questions.map((question, index) => {
        const answer = answers[question.question_id] ?? null;
        const qid = `${idPrefix}-q-${question.question_id}`;
        const options = question.options ?? [];
        const candidates = question.candidates ?? [];
        const optional = question.required === false;
        return (
          <li key={question.question_id} className="border-l border-border/70 pl-3">
            <p className="text-[13px] font-semibold leading-snug text-foreground">
              <span className="mr-1.5 tabular-nums text-muted-foreground">{index + 1}.</span>
              {question.prompt}
              {optional && (
                <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">(optional)</span>
              )}
            </p>
            {question.detail && (
              <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{question.detail}</p>
            )}
            {question.why && (
              <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                <span className="font-medium text-foreground">Why: </span>
                {question.why}
              </p>
            )}

            {options.length > 0 && (
              <div className="mt-2">
                <OptionCards
                  ariaLabel={question.prompt}
                  options={options}
                  value={answer?.option_id ?? null}
                  onChange={(optionId) => onChange(question.question_id, { option_id: optionId })}
                  disabled={disabled}
                  idPrefix={qid}
                />
              </div>
            )}

            {candidates.length > 0 && (
              <div className="mt-2">
                {options.length > 0 && (
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Or pick from your org
                  </p>
                )}
                <OptionCards
                  ariaLabel={`${question.prompt} — candidates`}
                  options={candidatesAsOptions(candidates)}
                  value={answer?.ref_id ?? null}
                  onChange={(refId) => onChange(question.question_id, { ref_id: refId })}
                  disabled={disabled}
                  idPrefix={`${qid}-cand`}
                />
              </div>
            )}

            {question.allow_free_text !== false && (
              <details className="group mt-2" open={options.length === 0 && candidates.length === 0}>
                <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <ChevronRight
                    aria-hidden
                    className="h-3 w-3 transition-transform group-open:rotate-90 motion-reduce:transition-none"
                  />
                  {options.length || candidates.length ? "Answer in your own words" : "Your answer"}
                </summary>
                <div className="mt-1.5">
                  <label htmlFor={`${qid}-text`} className="sr-only">
                    {question.prompt}
                  </label>
                  <Textarea
                    id={`${qid}-text`}
                    value={answer?.free_text ?? ""}
                    onChange={(e) => {
                      const text = e.target.value;
                      onChange(question.question_id, text.trim() ? { free_text: text.slice(0, FREE_TEXT_MAX) } : null);
                    }}
                    maxLength={FREE_TEXT_MAX}
                    rows={2}
                    disabled={disabled}
                    placeholder="Type an answer…"
                    className="text-[13px]"
                  />
                </div>
              </details>
            )}

            {question.assumption_if_unanswered && (
              <p className="mt-1.5 text-[12px] leading-snug text-muted-foreground">
                <span className="font-medium">If you skip this: </span>
                {question.assumption_if_unanswered}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
